import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, LogIn, Layers, Shield, Zap, Users, Loader2, AlertCircle, ArrowLeft, Building2, Mail } from 'lucide-react';
import { useAuth, useTenant } from '@/contexts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

type LoginStep = 'email' | 'password';

export function Login() {
  const navigate = useNavigate();
  const { discoverTenant, login } = useAuth();
  const { tenant, clearTenant } = useTenant();
  const [step, setStep] = useState<LoginStep>('email');
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Dynamic branding values
  const brandColor = tenant?.primaryColor || 'var(--dg-brand)';
  const brandName = tenant?.companyName || tenant?.name || 'DestekGör';
  const brandLogo = tenant?.logoUrl;

  // Detect if this is a white-label access (custom domain or tenant subdomain)
  const hostname = window.location.hostname;
  const isKnownDomain = ['destekgor.com', 'localhost'].some(
    d => hostname === d || hostname.endsWith(`.${d}`)
  );
  const isCustomDomain = !isKnownDomain;
  // Check if accessing via tenant subdomain (e.g., newton.destekgor.com)
  const isSubdomain = isKnownDomain && hostname !== 'localhost' &&
    !['www', 'app', 'api', 'portal', 'admin'].some(r => hostname.startsWith(`${r}.`)) &&
    hostname.split('.').length >= 3;
  // Show DestekGör marketing content only on the main platform domain
  const showMarketingContent = !isCustomDomain && !isSubdomain;

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const result = await discoverTenant(email);

    if (result) {
      setStep('password');
    } else {
      setError('Bu e-posta adresi ile kayıtlı bir hesap bulunamadı');
    }

    setIsLoading(false);
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const result = await login(email, password);

    if (result.ok) {
      navigate('/');
    } else {
      setError(result.errorMessage ?? 'Şifre hatalı');
    }

    setIsLoading(false);
  };

  const handleBack = () => {
    setStep('email');
    setPassword('');
    setError('');
    // Custom domain'de tenant context'i koruyarak sadece discover state'ini temizle
    if (!isCustomDomain) {
      clearTenant();
    }
  };

  const features = [
    { icon: Shield, title: 'Güvenli', desc: 'RBAC yetkilendirme' },
    { icon: Zap, title: 'Hızlı', desc: 'Anında erişim' },
    { icon: Users, title: 'Ekip', desc: 'Takım çalışma' },
    { icon: Layers, title: 'Modular', desc: 'Multi-tenant' },
  ];

  return (
    <div className="flex min-h-screen">
      {/* Left Side - Branding */}
      <div
        className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-between relative overflow-hidden"
        style={{ backgroundColor: brandColor }}
      >
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -left-20 size-96 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-20 -right-20 size-96 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-[600px] rounded-full bg-black/5 blur-3xl" />
        </div>

        {/* Logo */}
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            {brandLogo ? (
              <img src={brandLogo} alt={brandName} className="size-12 rounded-xl object-cover" />
            ) : (
              <div className="flex size-12 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
                <Layers className="size-7 text-white" />
              </div>
            )}
            <span className="text-3xl font-bold text-white">{brandName}</span>
          </div>
        </div>

        {/* Main Content */}
        <div className="relative z-10 space-y-8">
          {showMarketingContent ? (
            <>
              <div>
                <h1 className="text-4xl font-bold text-white leading-tight">
                  IT Şirketleri İçin<br />
                  Profesyonel<br />
                  Yönetim Platformu
                </h1>
                <p className="mt-4 text-lg text-white/70">
                  Müşterilerinizi, ticket'larınızı ve ekibinizi tek bir yerden yönetin.
                </p>
              </div>

              {/* Features */}
              <div className="grid grid-cols-2 gap-4">
                {features.map((feature) => (
                  <div
                    key={feature.title}
                    className="flex items-center gap-3 rounded-xl bg-white/10 backdrop-blur-sm p-4 border border-white/10"
                  >
                    <div className="rounded-lg bg-white/20 p-2">
                      <feature.icon className="size-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{feature.title}</p>
                      <p className="text-sm text-white/60">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div>
              <h1 className="text-4xl font-bold text-white leading-tight">
                {brandName}
              </h1>
              <p className="mt-4 text-lg text-white/70">
                Yönetim paneline hoş geldiniz.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="relative z-10">
          <p className="text-sm text-white/60">
            {new Date().getFullYear()} {brandName}. Tüm hakları saklıdır.
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex w-full lg:w-1/2 items-center justify-center bg-slate-50 p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="mb-8 text-center lg:hidden">
            <div className="inline-flex items-center gap-3">
              {brandLogo ? (
                <img src={brandLogo} alt={brandName} className="size-10 rounded-xl object-cover" />
              ) : (
                <div
                  className="flex size-10 items-center justify-center rounded-xl"
                  style={{ backgroundColor: brandColor }}
                >
                  <Layers className="size-6 text-white" />
                </div>
              )}
              <span className="text-2xl font-bold" style={{ color: brandColor }}>
                {brandName}
              </span>
            </div>
          </div>

          {/* Login Card */}
          <Card className="border-slate-200/50 shadow-xl shadow-slate-200/50">
            <CardHeader className="space-y-1 pb-4">
              {step === 'password' && tenant && (
                <button
                  onClick={handleBack}
                  className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 transition-colors mb-2 -ml-1"
                >
                  <ArrowLeft className="size-4" />
                  Geri
                </button>
              )}
              <CardTitle className="text-2xl">
                {step === 'email' ? 'Hoş Geldiniz' : 'Giriş Yap'}
              </CardTitle>
              <CardDescription>
                {step === 'email'
                  ? 'Hesabınıza giriş yapmak için e-posta adresinizi girin'
                  : 'Şifrenizi girerek devam edin'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Step 1: Email Discovery */}
              {step === 'email' && (
                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  {/* Error Message */}
                  {error && (
                    <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-100 p-3 text-sm text-red-600">
                      <AlertCircle className="size-4 shrink-0" />
                      {error}
                    </div>
                  )}

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email">E-posta Adresi</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="ornek@sirket.com"
                        className="h-11 pl-10"
                        required
                        autoFocus
                      />
                    </div>
                  </div>

                  {/* Submit */}
                  <Button
                    type="submit"
                    disabled={isLoading || !email}
                    className="w-full h-11 shadow-lg"
                    style={{ backgroundColor: brandColor, boxShadow: `0 10px 15px -3px ${brandColor}40` }}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="size-5 animate-spin" />
                        Kontrol ediliyor...
                      </>
                    ) : (
                      'Devam Et'
                    )}
                  </Button>
                </form>
              )}

              {/* Step 2: Password Entry */}
              {step === 'password' && tenant && (
                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                  {/* Tenant Info */}
                  <div className="flex items-center gap-3 rounded-lg bg-slate-50 border border-slate-200 p-3">
                    <div
                      className="flex size-10 items-center justify-center rounded-lg"
                      style={{ backgroundColor: `${brandColor}15` }}
                    >
                      {tenant.logoUrl ? (
                        <img src={tenant.logoUrl} alt={tenant.name} className="size-8 rounded object-cover" />
                      ) : (
                        <Building2 className="size-5" style={{ color: brandColor }} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{tenant.name}</p>
                      <p className="text-sm text-slate-500 truncate">{email}</p>
                    </div>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-100 p-3 text-sm text-red-600">
                      <AlertCircle className="size-4 shrink-0" />
                      {error}
                    </div>
                  )}

                  {/* Password */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Şifre</Label>
                      <a
                        href="#"
                        className="text-sm font-medium hover:opacity-80"
                        style={{ color: brandColor }}
                      >
                        Şifremi Unuttum
                      </a>
                    </div>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="********"
                        className="h-11 pr-11"
                        required
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        {showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Remember Me */}
                  <div className="flex items-center space-x-2">
                    <Checkbox id="remember" />
                    <label htmlFor="remember" className="text-sm text-slate-600 cursor-pointer">
                      Beni hatırla
                    </label>
                  </div>

                  {/* Submit */}
                  <Button
                    type="submit"
                    disabled={isLoading || !password}
                    className="w-full h-11 shadow-lg"
                    style={{ backgroundColor: brandColor, boxShadow: `0 10px 15px -3px ${brandColor}40` }}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="size-5 animate-spin" />
                        Giriş yapılıyor...
                      </>
                    ) : (
                      <>
                        <LogIn className="size-5" />
                        Giriş Yap
                      </>
                    )}
                  </Button>
                </form>
              )}

            </CardContent>
          </Card>

          {/* Footer */}
          <p className="mt-8 text-center text-sm text-slate-500">
            Henüz hesabınız yok mu?{' '}
            <a href="#" className="font-medium hover:opacity-80" style={{ color: brandColor }}>
              İletişime geçin
            </a>
          </p>

          {isCustomDomain && (
            <p className="mt-4 text-center text-xs text-slate-400">
              Powered by{' '}
              <a href="https://destekgor.com" target="_blank" rel="noopener noreferrer" className="font-medium text-slate-500 hover:text-slate-600">
                DestekGör
              </a>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
